import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import InvoiceSelectionModal from './InvoiceSelectionModal';
import { CreditNoteCard } from './ApplyToInvoiceModal/CreditNoteCard';
import { CreditApplicationControls } from './ApplyToInvoiceModal/CreditApplicationControls';
import { CreditApplicationSummary } from './ApplyToInvoiceModal/CreditApplicationSummary';
import { useCreditNoteApplications } from './ApplyToInvoiceModal/useCreditNoteApplications';
import { IApplyToInvoiceModalProps } from './ApplyToInvoiceModal/types';

export const ApplyToInvoiceModal: React.FC<IApplyToInvoiceModalProps> = ({
  open,
  onClose,
  selectedCreditNotes,
  onSuccess,
}) => {
  const {
    // State
    creditNoteApplications,
    applicationDate,
    setApplicationDate,
    isSubmitting,
    error,
    success,
    invoiceSelectionOpen,

    // Functions
    getAvailableCreditAmount,
    updateInvoiceApplicationAmount,
    removeInvoiceApplication,
    toggleCreditNoteExpansion,
    openInvoiceSelection,
    closeInvoiceSelection,
    handleInvoicesSelected,
    handleSubmit,
    removeAllInvoices,
    addAllSourceInvoices,
    hasAnyInvoiceApplications,
    getTotalAvailableCredit,
    getTotalApplyingAmount,
    getTotalRemainingCredit,
    getSubmitValidationError,
  } = useCreditNoteApplications(selectedCreditNotes, open, onClose, onSuccess);

  const validationError = getSubmitValidationError();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Apply Credit Notes to Invoices</DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {(error || success) && (
            <Snackbar open={Boolean(error || success)} autoHideDuration={6000}>
              <Alert severity={error ? 'error' : 'success'}>
                {error || success}
              </Alert>
            </Snackbar>
          )}

          <CreditApplicationControls
            applicationDate={applicationDate}
            setApplicationDate={setApplicationDate}
            isSubmitting={isSubmitting}
            hasAnyInvoiceApplications={hasAnyInvoiceApplications()}
            onRemoveAllInvoices={removeAllInvoices}
            onAddAllSourceInvoices={addAllSourceInvoices}
            error={error}
            validationError={validationError}
          />

          <CreditApplicationSummary
            totalAvailable={getTotalAvailableCredit()}
            totalApplying={getTotalApplyingAmount()}
            totalRemaining={getTotalRemainingCredit()}
          />

          <Box sx={{ mt: 3 }}>
            {creditNoteApplications.length > 0
              ? creditNoteApplications.map((creditNoteApp) => (
                <CreditNoteCard
                  key={creditNoteApp.creditNoteId}
                  creditNoteApplication={creditNoteApp}
                  availableCreditAmount={getAvailableCreditAmount(creditNoteApp.creditNote)}
                  onToggleExpansion={() => toggleCreditNoteExpansion(creditNoteApp.creditNoteId)}
                  onUpdateAmount={updateInvoiceApplicationAmount}
                  onRemoveInvoice={removeInvoiceApplication}
                  onOpenInvoiceSelection={() => openInvoiceSelection(creditNoteApp.creditNoteId)}
                  isSubmitting={isSubmitting}
                />
              ))
              : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  <Typography variant="body2"> Loading credit note applications...</Typography>
                </Box>
              )}
          </Box>
        </DialogContent>

        <DialogActions>
          <CreditApplicationControls
            applicationDate={applicationDate}
            setApplicationDate={setApplicationDate}
            isSubmitting={isSubmitting}
            hasAnyInvoiceApplications={hasAnyInvoiceApplications()}
            onRemoveAllInvoices={removeAllInvoices}
            onAddAllSourceInvoices={addAllSourceInvoices}
            onCancel={onClose}
            onSubmit={handleSubmit}
            submitDisabled={isSubmitting || creditNoteApplications.length === 0}
            showOnlyActions
            error={error}
            validationError={validationError}
          />
        </DialogActions>
      </Dialog>

      <InvoiceSelectionModal
        open={invoiceSelectionOpen}
        onClose={closeInvoiceSelection}
        onInvoicesSelected={handleInvoicesSelected}
        billingAccountId={selectedCreditNotes[0]?.billingAccountId}
      />
    </LocalizationProvider>
  );
};

export default ApplyToInvoiceModal;
